'use client';

import { cn } from '@lqa-frontend/ui-system';
import * as LabelPrimitive from '@radix-ui/react-label';
import * as React from 'react';
import { Icon } from '../icon';
import { SimpleTooltip } from '../tooltip';
import { BodyM, BodyS } from '../typography';

interface ILabelLink {
	/** Visible text of the secondary link. */
	label: string;
	/** Renders an anchor when provided. */
	href?: string;
	/** Renders a button-style link when no `href` is given. */
	onClick?: () => void;
}

interface ILabelProps extends React.ComponentProps<typeof LabelPrimitive.Root> {
	/** Secondary text link rendered next to the label for context/help. */
	link?: ILabelLink;
	/** Tooltip content surfaced through an info (i) icon next to the label. */
	info?: React.ReactNode;
	/** Appends a "(Required)" indicator after the label text. */
	isRequired?: boolean;
	/** Appends an "(Optional)" indicator after the label text. */
	isOptional?: boolean;
	/** Current character count shown on the far right as `count / max`. */
	charCount?: number;
	/** Maximum characters for the counter. */
	maxCount?: number;
}

function Label({
	className,
	children,
	link,
	info,
	isRequired,
	isOptional,
	charCount,
	maxCount,
	...props
}: ILabelProps) {
	const hasCounter = typeof charCount === 'number';

	return (
		<LabelPrimitive.Root
			data-slot="label"
			className={cn(
				'flex items-center gap-4 select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50',
				hasCounter && 'w-full justify-between',
				className
			)}
			{...props}
		>
			<div className="flex items-center gap-4">
				<BodyM fontWeight="regular">{children}</BodyM>
				{isRequired && (
					<BodyS className="self-end text-zinc-400">(Required)</BodyS>
				)}
				{isOptional && (
					<BodyS className="self-end text-zinc-400">(Optional)</BodyS>
				)}
				{info && (
					<SimpleTooltip content={info} triggerClassName="text-interactive">
						<Icon name="infoCircle" size="s" aria-label="More information" />
					</SimpleTooltip>
				)}
				{link &&
					(link.href ? (
						<a
							href={link.href}
							onClick={link.onClick}
							className="inline-flex items-center self-end hover:underline"
						>
							<BodyS color="link">{link.label}</BodyS>
						</a>
					) : (
						<button
							type="button"
							onClick={link.onClick}
							className="inline-flex items-center self-end hover:underline"
						>
							<BodyS color="link">{link.label}</BodyS>
						</button>
					))}
			</div>
			{hasCounter && (
				<BodyS color="neutral" className="tabular-nums text-zinc-400 self-end">
					{charCount}
					{typeof maxCount === 'number' && ` / ${maxCount}`}
				</BodyS>
			)}
		</LabelPrimitive.Root>
	);
}

export { Label };
export type { ILabelProps, ILabelLink };
